import { QSetStatus } from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import {
  ContentCopyRoundedIcon,
  DeleteOutlineIcon,
  FileDownloadOutlinedIcon,
  MoreMenuIconButton,
  type MoreMenuIconButtonOption,
} from '@shared/components';
import { usePermission } from '@shared/permissions';
import { useCallback, useMemo } from 'react';

import { useQSetHeaderModal, QSetDetailsHeaderModalAction } from '../../providers';

interface MoreActionMenuProps {
  qSetId: string;
  status: QSetStatus;
}

interface MoreActionMenuOption extends MoreMenuIconButtonOption {
  status: QSetStatus[];
  requiredPermission: string[];
}

export default function MoreActionMenu(props: Readonly<MoreActionMenuProps>) {
  const { status } = props;
  const { permissions } = usePermission();
  const { setAction } = useQSetHeaderModal();

  const handleDuplicate = useCallback(() => {
    setAction(QSetDetailsHeaderModalAction.DUPLICATE);
  }, []);

  const handleExport = useCallback(() => {
    setAction(QSetDetailsHeaderModalAction.EXPORT);
  }, []);

  const handleDelete = useCallback(() => {
    setAction(QSetDetailsHeaderModalAction.DELETE);
  }, []);

  const options: MoreActionMenuOption[] = useMemo(
    () => [
      {
        label: 'Duplicate',
        icon: <ContentCopyRoundedIcon />,
        onClick: handleDuplicate,
        requiredPermission: [APP_PERMISSIONS.EXAM.QSET.CREATE, APP_PERMISSIONS.EXAM.QCONFIG.VIEW],
        status: [
          QSetStatus.Draft,
          QSetStatus.Published,
          QSetStatus.Unpublished,
          QSetStatus.Reviewed,
          QSetStatus.Disabled,
        ],
      },
      {
        label: 'Export QSet',
        icon: <FileDownloadOutlinedIcon />,
        onClick: handleExport,
        requiredPermission: [],
        status: [
          QSetStatus.Draft,
          QSetStatus.Published,
          QSetStatus.Unpublished,
          QSetStatus.InReview,
          QSetStatus.Reviewed,
        ],
      },
      {
        label: 'Delete',
        icon: (
          <DeleteOutlineIcon
            sx={(theme) => ({
              color: theme.palette.error.main,
            })}
          />
        ),
        onClick: handleDelete,
        requiredPermission: [APP_PERMISSIONS.EXAM.QSET.DELETE],
        status: [QSetStatus.Draft],
        sx: (theme) => ({
          color: theme.palette.error.main,
        }),
      },
    ],
    [handleDuplicate, handleExport],
  );

  const filteredOptions = useMemo(
    () =>
      options.filter(
        (option) =>
          option.status.includes(status) &&
          option.requiredPermission.every((reqPermission) => permissions.includes(reqPermission)),
      ),
    [options, status, permissions],
  );

  return (
    <>
      <MoreMenuIconButton
        options={filteredOptions}
        slotProps={{
          iconButton: {
            sx: (theme) => ({
              borderRadius: theme.shape.rounded(1),
              border: `1px solid ${theme.palette.grey[400]}`,
            }),
          },
        }}
      />
    </>
  );
}
